import { useState, useEffect } from 'react';
import { getStats, runBatch, getFeedback } from '../api';

const CATEGORY_META = {
  support:  { label: 'Support',         color: '#007AFF', bg: '#EFF6FF' },
  bug:      { label: 'Bug',             color: '#FF3B30', bg: '#FFF0EF' },
  feature:  { label: 'Feature Request', color: '#00C48C', bg: '#E6FAF5' },
  feedback: { label: 'Feedback',        color: '#FF9500', bg: '#FFF8EC' },
};

const Icon = ({ type, color }) => {
  const c = color || '#007AFF';
  const icons = {
    users:    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="8" cy="6" r="3" stroke={c} strokeWidth="1.5"/><path d="M2 17c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke={c} strokeWidth="1.5" strokeLinecap="round"/><circle cx="15" cy="6" r="2" stroke={c} strokeWidth="1.5"/><path d="M17 17c0-2.209-1.343-4-3-4" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>,
    tx:       <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3 10h14M13 6l4 4-4 4M7 6L3 10l4 4" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    volume:   <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><polyline points="2,14 7,8 11,11 18,4" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><polyline points="14,4 18,4 18,8" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    fees:     <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" stroke={c} strokeWidth="1.5"/><path d="M10 6v8M7.5 8.5C7.5 7.4 8.6 7 10 7s2.5.4 2.5 1.5S11.4 10 10 10s-2.5.4-2.5 1.5S8.6 13 10 13s2.5-.4 2.5-1.5" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>,
    chart:    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="12" width="3" height="6" rx="1" fill={c}/><rect x="8" y="8" width="3" height="10" rx="1" fill={c}/><rect x="14" y="4" width="3" height="14" rx="1" fill={c}/></svg>,
    calendar: <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="4" width="16" height="14" rx="2" stroke={c} strokeWidth="1.5"/><path d="M2 8h16M6 2v4M14 2v4" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>,
    btc:      <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" stroke={c} strokeWidth="1.5"/><path d="M10 5v10M7 7.5h4a1.5 1.5 0 010 3H7h4.5a1.5 1.5 0 010 3H7" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>,
    chat:     <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M18 13a2 2 0 01-2 2H6l-3 3V4a2 2 0 012-2h11a2 2 0 012 2z" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  };
  return icons[type] || null;
};

export default function DashboardPage() {
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [batching, setBatching] = useState(false);
  const [msg, setMsg]           = useState('');
  const [msgType, setMsgType]   = useState('success');
  const [feedback, setFeedback] = useState([]);
  const [fbLoading, setFbLoading] = useState(true);
  const [fbFilter, setFbFilter]   = useState('');

  useEffect(() => {
    getStats().then(setStats).catch(console.error).finally(() => setLoading(false));
    getFeedback({ limit: 20 }).then(d => setFeedback(d.items || [])).catch(console.error).finally(() => setFbLoading(false));
  }, []);

  async function handleBatch() {
    setBatching(true); setMsg('');
    try {
      const res = await runBatch();
      setMsgType('success');
      setMsg(res.skipped ? 'No pending saves — batch skipped' :
             `Batch complete — ${res.userCount} users, $${res.totalUsd} processed`);
    } catch (err) {
      setMsgType('error');
      setMsg('Batch failed: ' + err.message);
    } finally { setBatching(false); }
  }

  if (loading) return <div style={s.loading}>Loading...</div>;

  const statCards = [
    { label:'Total Users',        value: stats?.users?.total || 0,             icon:'users',    color:'#007AFF' },
    { label:'Total Transactions', value: stats?.transactions?.total || 0,      icon:'tx',       color:'#00C48C' },
    { label:'Today Volume',       value: '$'+(stats?.today?.volume || '0.00'), icon:'volume',   color:'#FF9500' },
    { label:'Today Fees',         value: '$'+(stats?.today?.fees || '0.00'),   icon:'fees',     color:'#00C48C' },
    { label:'Week Volume',        value: '$'+(stats?.week?.volume || '0.00'),  icon:'chart',    color:'#007AFF' },
    { label:'Week Fees',          value: '$'+(stats?.week?.fees || '0.00'),    icon:'fees',     color:'#00C48C' },
    { label:'Month Volume',       value: '$'+(stats?.month?.volume || '0.00'), icon:'calendar', color:'#FF9500' },
    { label:'Month Fees',         value: '$'+(stats?.month?.fees || '0.00'),   icon:'fees',     color:'#00C48C' },
    { label:'Pending BTC Save',   value: '$'+(stats?.pendingSave || '0.00'),   icon:'btc',      color:'#FF9500' },
  ];

  const filteredFeedback = fbFilter ? feedback.filter(f => f.category === fbFilter) : feedback;

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Dashboard</h1>
          <p style={s.subtitle}>Platform overview and controls</p>
        </div>
        <button style={s.batchBtn} onClick={handleBatch} disabled={batching}>
          {batching ? 'Running...' : '⚡ Run Daily Batch'}
        </button>
      </div>

      {msg && (
        <div style={{...s.alert, ...(msgType==='error' ? s.alertError : s.alertSuccess)}}>
          {msg}
        </div>
      )}

      <div style={s.grid}>
        {statCards.map(c => (
          <div key={c.label} style={s.statCard}>
            <div style={{...s.statIconWrap, background: c.color+'15'}}>
              <Icon type={c.icon} color={c.color} />
            </div>
            <div style={s.statValue}>{c.value}</div>
            <div style={s.statLabel}>{c.label}</div>
          </div>
        ))}
      </div>

      {stats?.lastBatch && (
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Last Batch</h2>
          <div style={s.card}>
            <div style={s.cardRow}>
              <span style={s.cardLabel}>Status</span>
              <span style={{...s.badge,
                background: stats.lastBatch.status==='completed' ? '#E6FAF5' : '#FFF0EF',
                color:      stats.lastBatch.status==='completed' ? '#00C48C' : '#FF3B30'}}>
                {stats.lastBatch.status}
              </span>
            </div>
            <div style={s.cardRow}>
              <span style={s.cardLabel}>Total USD</span>
              <span style={s.cardValue}>${(stats.lastBatch.total_usd_cents/100).toFixed(2)}</span>
            </div>
            {stats.lastBatch.btc_price_cents && (
              <div style={s.cardRow}>
                <span style={s.cardLabel}>BTC Price</span>
                <span style={s.cardValue}>${(stats.lastBatch.btc_price_cents/100).toLocaleString()}</span>
              </div>
            )}
            {stats.lastBatch.tx_hash && (
              <div style={s.cardRow}>
                <span style={s.cardLabel}>TX Hash</span>
                <a href={`https://basescan.org/tx/${stats.lastBatch.tx_hash}`}
                  target="_blank" rel="noreferrer" style={s.link}>
                  {stats.lastBatch.tx_hash.slice(0,24)}...
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Feedback Section */}
      <div style={s.section}>
        <div style={s.fbHeader}>
          <div style={{display:'flex', alignItems:'center', gap:10}}>
            <div style={{...s.statIconWrap, background:'#007AFF15', marginBottom:0}}>
              <Icon type="chat" color="#007AFF"/>
            </div>
            <div>
              <h2 style={{...s.sectionTitle, marginBottom:0}}>User Feedback</h2>
              <div style={{fontSize:12, color:'#6E6E73'}}>{feedback.length} recent messages</div>
            </div>
          </div>
          <div style={s.fbFilters}>
            {['', 'support', 'bug', 'feature', 'feedback'].map(f => (
              <button key={f} onClick={() => setFbFilter(f)}
                style={{
                  ...s.fbPill,
                  background: fbFilter === f ? '#007AFF' : '#F5F5F7',
                  color:      fbFilter === f ? '#fff'    : '#1D1D1F',
                  border:     `1.5px solid ${fbFilter === f ? '#007AFF' : '#E5E5EA'}`,
                }}>
                {f === '' ? 'All' : CATEGORY_META[f]?.label || f}
              </button>
            ))}
          </div>
        </div>

        <div style={s.card}>
          {fbLoading ? (
            <div style={s.fbEmpty}>Loading...</div>
          ) : filteredFeedback.length === 0 ? (
            <div style={s.fbEmpty}>No messages yet</div>
          ) : (
            filteredFeedback.map((item, i) => {
              const meta = CATEGORY_META[item.category] || CATEGORY_META.feedback;
              return (
                <div key={item.id} style={{...s.fbRow, borderBottom: i < filteredFeedback.length-1 ? '1px solid #F5F5F7' : 'none'}}>
                  <div style={s.fbTop}>
                    <div style={s.fbUser}>
                      <div style={s.fbName}>{item.userName || 'Unknown'}</div>
                      {item.userEmail && <div style={s.fbEmail}>{item.userEmail}</div>}
                    </div>
                    <div style={{display:'flex', alignItems:'center', gap:8}}>
                      <span style={{...s.catBadge, background: meta.bg, color: meta.color}}>
                        {meta.label}
                      </span>
                      <span style={s.fbTime}>
                        {new Date(item.createdAt * 1000).toLocaleDateString('en-US', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}
                      </span>
                    </div>
                  </div>
                  <div style={s.fbMessage}>{item.message}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  loading:      { padding:40, color:'#6E6E73' },
  header:       { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 },
  title:        { fontSize:28, fontWeight:700, color:'#1D1D1F' },
  subtitle:     { fontSize:14, color:'#6E6E73', marginTop:4 },
  batchBtn:     { background:'#007AFF', color:'#fff', border:'none', padding:'12px 20px', borderRadius:12, fontSize:14, fontWeight:600, cursor:'pointer', boxShadow:'0 4px 12px rgba(0,122,255,0.3)' },
  alert:        { borderRadius:12, padding:'12px 16px', marginBottom:20, fontSize:14, fontWeight:500 },
  alertSuccess: { background:'#E6FAF5', border:'1px solid #B3F0DE', color:'#00A86B' },
  alertError:   { background:'#FFF0EF', border:'1px solid #FFD0CE', color:'#FF3B30' },
  grid:         { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:16, marginBottom:32 },
  statCard:     { background:'#fff', borderRadius:16, padding:20, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  statIconWrap: { width:40, height:40, borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:12 },
  statValue:    { fontSize:22, fontWeight:700, color:'#1D1D1F', marginBottom:4 },
  statLabel:    { fontSize:12, color:'#6E6E73' },
  section:      { marginTop:8, marginBottom:32 },
  sectionTitle: { fontSize:18, fontWeight:600, marginBottom:12, color:'#1D1D1F' },
  card:         { background:'#fff', borderRadius:16, padding:20, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' },
  cardRow:      { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #F5F5F7' },
  cardLabel:    { fontSize:14, color:'#6E6E73' },
  cardValue:    { fontSize:14, fontWeight:600 },
  badge:        { fontSize:12, fontWeight:600, padding:'4px 10px', borderRadius:8 },
  link:         { fontSize:13, color:'#007AFF', textDecoration:'none', fontFamily:'monospace' },
  fbHeader:     { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, flexWrap:'wrap', gap:12 },
  fbFilters:    { display:'flex', gap:6, flexWrap:'wrap' },
  fbPill:       { fontSize:12, fontWeight:500, padding:'5px 12px', borderRadius:20, cursor:'pointer' },
  fbRow:        { padding:'14px 0' },
  fbTop:        { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 },
  fbUser:       {},
  fbName:       { fontSize:14, fontWeight:600, color:'#1D1D1F' },
  fbEmail:      { fontSize:12, color:'#6E6E73' },
  fbTime:       { fontSize:11, color:'#AEAEB2' },
  fbMessage:    { fontSize:14, color:'#3D3D3D', lineHeight:1.5 },
  fbEmpty:      { padding:'20px 0', textAlign:'center', color:'#6E6E73', fontSize:14 },
  catBadge:     { fontSize:11, fontWeight:600, padding:'3px 8px', borderRadius:6 },
};
